import { useAuthResetPassword } from '@/api/gen-new/auth/auth';
import AuthFormLayout from '@/auth/components/AuthFormLayout';
import { TwoFactorCodeInput } from '@/auth/components/TwoFactorCodeInput';
import { isCompleteTwoFactorCode, twoFactorRequestErrorMessage } from '@/auth/twoFactor';
import { routeLogin } from '@/auth/constants/authSettings';
import useAuth from '@/auth/useAuth';
import requirements, { yupSchema } from '@/auth/constants/passwordRequirements';
import { CountdownText } from '@/components/displays/CountdownText/CountdownText';
import defaultNotifications from '@/components/functions/defaultNotifications';
import { MFA_CODE_EXPIRATION_SECONDS, MFA_CODE_LENGTH } from '@/constants/mfa';
import RequirementDisplay from '@/components/displays/RequirementDisplay';
import { validatePassword } from '@/utils/validatePassword';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Group, PasswordInput, PinInput, Popover, Progress, Stack, Text } from '@mantine/core';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import * as yup from 'yup';

const schema = yup.object({
	newPassword: yupSchema,
	confirmNewPassword: yup
		.string()
		.required('errors.confirmNewPasswordRequired')
		.oneOf([yup.ref('newPassword')], 'errors.confirmNewPasswordMustMatch'),
	mfaCode: yup.string().required('errors.mfaCodeRequired').length(6, 'errors.mfaCodeInvalid'),
});

export type ResetPasswordPageState = {
	email?: string;
};

type FormData = yup.InferType<typeof schema>;

function ResetPasswordPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const receivedState = location.state as ResetPasswordPageState;
	const { isAuthenticated, signOut } = useAuth();
	// Set once the API says the account has 2FA. The email code alone is then not enough.
	const [needsTwoFactorCode, setNeedsTwoFactorCode] = useState(false);
	const [twoFactorCode, setTwoFactorCode] = useState('');
	const [twoFactorError, setTwoFactorError] = useState<string>();
	// Remounts the countdown. The API restarts the email code's expiry when it asks for the second code.
	const [countdownKey, setCountdownKey] = useState(0);

	const { mutateAsync: resetPassword } = useAuthResetPassword({
		mutation: {
			onSuccess: () => {
				// The reset revoked every session, this one included, so keeping it would let the
				// login page send the user back in until the next token refresh failed.
				if (isAuthenticated()) {
					signOut();
				}
				defaultNotifications.showSuccess({
					message: t('resetPassword.success.passwordHasBeenReset'),
				});
				navigate(routeLogin);
			},
			onError: error => {
				const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
				if (message === 'auth.two.factor.code.required') {
					setNeedsTwoFactorCode(true);
					setCountdownKey(key => key + 1);
					return;
				}
				if (message?.startsWith('auth.two.factor.')) {
					setTwoFactorError(twoFactorRequestErrorMessage(error));
					setTwoFactorCode('');
					return;
				}
				defaultNotifications.showError({
					message: t('resetPassword.errors.invalidCode'),
				});
			},
		},
	});

	const [showRequirements, setShowRequirements] = useState(false);
	const [isConfirmFocused, setIsConfirmFocused] = useState(false);
	const [isExpired, setIsExpired] = useState(false);

	useEffect(() => {
		if (!receivedState?.email) {
			navigate('/forgot-password', {
				replace: true,
			});
		}
	}, [navigate, receivedState?.email]);

	const {
		control,
		register,
		handleSubmit,
		formState: { errors, isSubmitting, isValid },
		watch,
	} = useForm<FormData>({
		resolver: yupResolver(schema),
	});

	const onSubmit = async ({ newPassword, mfaCode }: FormData) => {
		if (!receivedState?.email || !mfaCode) {
			// Safe guard, this should not happen due to the useEffect above
			return;
		}

		await resetPassword({
			data: {
				email: receivedState.email,
				mfaCode,
				password: newPassword,
				twoFactorCode: needsTwoFactorCode ? twoFactorCode.trim() : undefined,
			},
		}).catch(() => undefined);
	};

	const value = watch('newPassword') ?? '';
	const confirmValue = watch('confirmNewPassword') ?? '';
	const { passwordColor, passwordStrength, confirmColor, confirmStrength, doesConfirmMatch } = validatePassword(
		value,
		confirmValue,
	);

	return (
		<AuthFormLayout
			title={t('resetPassword.title')}
			onSubmit={handleSubmit(onSubmit)}
			fields={
				<Stack>
					{/* Tells password managers which saved login the new password replaces. */}
					<input type="text" autoComplete="username" value={receivedState?.email ?? ''} readOnly hidden />
					<Popover opened={showRequirements} position="right" width="target" transitionProps={{ transition: 'pop' }}>
						<Popover.Target>
							<div onFocusCapture={() => setShowRequirements(true)} onBlurCapture={() => setShowRequirements(false)}>
								<PasswordInput
									{...register('newPassword')}
									withAsterisk
									label={t('resetPassword.newPassword')}
									autoComplete="new-password"
									disabled={isExpired}
									error={errors.newPassword && t(`resetPassword.${errors.newPassword.message}`)}
								/>
							</div>
						</Popover.Target>
						<Popover.Dropdown>
							<Progress color={passwordColor} value={passwordStrength} size={5} mb="xs" />
							{requirements.map(req => (
								<RequirementDisplay key={req.key} label={t(`resetPassword.${req.key}`)} meets={req.re.test(value)} />
							))}
						</Popover.Dropdown>
					</Popover>
					<Popover opened={isConfirmFocused} position="right" width="target" transitionProps={{ transition: 'pop' }}>
						<Popover.Target>
							<div onFocusCapture={() => setIsConfirmFocused(true)} onBlurCapture={() => setIsConfirmFocused(false)}>
								<PasswordInput
									label={t('resetPassword.confirmNewPassword')}
									autoComplete="new-password"
									{...register('confirmNewPassword')}
									disabled={isExpired}
									withAsterisk
									error={errors.confirmNewPassword && t(`resetPassword.${errors.confirmNewPassword.message}`)}
								/>
							</div>
						</Popover.Target>
						<Popover.Dropdown>
							<Progress color={confirmColor} value={confirmStrength} size={5} mb="xs" />
							<RequirementDisplay
								label={t('resetPassword.errors.confirmNewPasswordMustMatch')}
								meets={doesConfirmMatch}
							/>
						</Popover.Dropdown>
					</Popover>
					<Stack gap={2} align="flex-start">
						<Group>
							<Group gap={2}>
								<Text size="sm">Secure Code</Text>
								<Text size="sm" c="red">
									*
								</Text>
							</Group>
						</Group>
						<Controller
							control={control}
							name="mfaCode"
							render={({ field }) => (
								<PinInput
									length={MFA_CODE_LENGTH}
									value={field.value}
									onChange={value => field.onChange(value)}
									error={!!errors.mfaCode}
									disabled={isExpired}
								/>
							)}
						/>
						{!isExpired && (
							<Group gap={4}>
								<CountdownText
									key={countdownKey}
									size="xs"
									fw="normal"
									c="gray.7"
									expiresInSeconds={MFA_CODE_EXPIRATION_SECONDS}
									onExpire={() => setIsExpired(true)}
									prefix="Secure code will expire in "
								/>
							</Group>
						)}
					</Stack>
					{needsTwoFactorCode && (
						<Stack gap={4}>
							<Text size="sm">{t('twoFactor.reset.description')}</Text>
							<TwoFactorCodeInput
								value={twoFactorCode}
								onChange={value => {
									setTwoFactorCode(value);
									setTwoFactorError(undefined);
								}}
								error={twoFactorError}
								disabled={isExpired}
							/>
						</Stack>
					)}
				</Stack>
			}
			actionButton={
				<>
					<Button
						type="submit"
						aria-disabled={isSubmitting || isExpired}
						disabled={
							isSubmitting || isExpired || !isValid || (needsTwoFactorCode && !isCompleteTwoFactorCode(twoFactorCode))
						}
						variant="filled"
					>
						{t('resetPassword.resetPassword')}
					</Button>
					{isExpired && (
						<Text
							component={Link}
							to="/forgot-password"
							size="sm"
							c="red.9"
							style={{
								textDecoration: 'underline',
								textDecorationLine: 'underline',
								textUnderlineOffset: '4px',
							}}
						>
							MFA code expired. Please restart the password reset process.
						</Text>
					)}
				</>
			}
			altAction={{
				preText: t('resetPassword.loginText'),
				text: t('resetPassword.login'),
				to: '/login',
			}}
		/>
	);
}

export default ResetPasswordPage;
